import qs from "qs";
import axios from "axios";
import { computed } from "vue";
import type { AxiosError, Method, ResponseType } from "axios";
import Message from "@/plugins/message";
import type { BaseResponse } from "@/types/base-response";
import { useUserStore } from "@/stores/user";
import { t } from '@/locale'

import {
  RequestErrorTypeEnum,
  HttpContentTypeEnum,
  ResponseStatusCodeEnum,
  AppEventTypeEnum,
} from "./enums";
import EventBus from "./eventbus";
import obj from "./object";
import { fileDownload,filterEmptyParam } from "./browser";
import URI from "./uri";

// const useSystem = useSystemStore();
/**
 *
 * @param {object} params 请求参数
 * @param {string} contentType request - contentType
 * @param {FormData} data 文件上传专用
 * @returns { object } axios params/data
 */
const getParamsByContentType = (params = {}, contentType = "", data = {}) => {
  let result = {};
  switch (contentType) {
    case HttpContentTypeEnum.FILE:
      result = data;
      break;
    case HttpContentTypeEnum.FORM:
      result = obj.objectToFormData(params);
      break;
    default:
      result = obj.deepClone(params);
  }
  return result;
};

/**
 * @param {object} res axios response
 */
const handleFileDownload = (
  res: any,
  resolve = (res: any) => {},
  reject = (res: any) => {}
) => {
  const {
    headers: { "content-disposition": disposition },
  } = res;
  if (disposition && /attachment/.test(disposition)) {
    fileDownload(res);
    resolve(res);
    return;
  }
  const reader: FileReader = new FileReader();
  reader.onload = (ev) => {
    const result = ((ev && ev.target && ev.target.result) || "{}") as string;
    const content = JSON.parse(result);
    Message.error({ message: content.comments || "下载失败" });
    reject(res);
  };
  reader.readAsText(res.data);
};

const handleCRUDLogic = (
  res: any,
  resolve = (res: any) => {},
  reject = (res: any) => {}
) => {
  const {
    data: response,
    data: { statusCode, comments, errorMsg },
  } = res;
  const message = comments || errorMsg || "服务异常";
  switch (statusCode) {
    case ResponseStatusCodeEnum.SUCCESS:
    case ResponseStatusCodeEnum.NO_PHOTO_CODE:
      resolve(response);
      break;
    case ResponseStatusCodeEnum.ERROR:
    case ResponseStatusCodeEnum.SERVER_ERROR:
      limitShowMessage(statusCode, message);
      reject(res);
      break;
    case ResponseStatusCodeEnum.SERVER_PARAMS_ERROR:
      limitShowMessage(statusCode, message);
      reject(res);
      break;
    case ResponseStatusCodeEnum.USER_NAME_NOEXIST:
      limitShowMessage(statusCode, message);
      reject(res);
      break;
    case ResponseStatusCodeEnum.SERVICE_ERROR:
      limitShowMessage(statusCode, message);
      reject(res);
      break;
    case ResponseStatusCodeEnum.TOKEN_INVALID:
      // 如果后端什么文案都没有提供，那么提示默认文案
      limitShowMessage(statusCode, t('system.relogin_comment'));
      EventBus.$emit(AppEventTypeEnum.RE_LOGIN);
      break;
    case ResponseStatusCodeEnum.USER_NOT_LOGIN:
      // 如果后端什么文案都没有提供，那么提示默认文案
      limitShowMessage(statusCode, t('system.relogin_comment'));
      EventBus.$emit(AppEventTypeEnum.RE_LOGIN);
      break;
    case ResponseStatusCodeEnum.TOKEN_PARSE_ERROR:
      EventBus.$emit(AppEventTypeEnum.RE_LOGIN);
      break;
    case ResponseStatusCodeEnum.TOKEN_EXPIRE:
      // 如果后端什么文案都没有提供，那么提示默认文案
      limitShowMessage(statusCode, t('system.relogin_comment'));
      EventBus.$emit(AppEventTypeEnum.RE_LOGIN);
      break;
    case ResponseStatusCodeEnum.AVOID_ERROR_MESSAGE:
      reject(res);
      break;
    default:
      limitShowMessage(statusCode, message);
      reject(res);
  }
};

/**
 * @module utils/request
 * @description - 与后端接口交互的封装, 基于axios,
 * 一般来讲http请求参数的key为params，
 * eg. request.httpPost(url, { params }); 文件上传比较特殊以 data 形式传递
 * eg. request.httpPost(url, { data, contentType:  HttpContentTypeEnum.FILE})
 * notFilterEmptyParam 是否过滤空字符串  true: 不过滤 false: 过滤
 */
interface requestOption {
  method: Method;
  params?: Record<string, unknown>;
  contentType?: string;
  data?: Record<string, unknown>;
  downloadMethod?: string;
  notFilterEmptyParam?: boolean;
  interceptorFlag?: boolean;
}

let interceptorFlag = false;
const source = axios.CancelToken.source();
function request<T>(
  url = "",
  options: requestOption = {
    method: "get",
    params: {},
    contentType: HttpContentTypeEnum.JSON,
    data: {},
    downloadMethod: "",
    notFilterEmptyParam: true,
    interceptorFlag: false,
  },
  customHeaders?:any
): Promise<BaseResponse<T>> {
  const {
    method,
    contentType = HttpContentTypeEnum.JSON,
    downloadMethod,
    notFilterEmptyParam,
  } = options;

  let data = {};
  // 多语言配置，不用store是由于第一次刷新后的请求store内还未存入curretnI18
  const system = JSON.parse(localStorage.getItem('system') || '{}');
  const lang = 'zh';
  let params: any = options?.params ? { ...options?.params, "lang": lang, ver: '4.24.0' } : { "lang": lang, ver: '4.24.0' };
  let plainParams: any = { "lang": lang };
  let plainData = {};
  let requestUrl = "";
  let responseType: ResponseType;
  let tempParams: { [index: string]: any } = {};
  let tempPramsKeys = [];
  let resetMethod: Method | string = "";
  interceptorFlag = !!options.interceptorFlag;

  // 图片上传应对方案,判断contentType为FILE时，取内部的data为请求参数
  if (contentType === HttpContentTypeEnum.FILE) {
    data = (params?.data || {}) as any;
    params = { "lang": lang };
  }

  switch (method) {
    case "post":
      plainData = getParamsByContentType(params, contentType, data);
      break;
    case "get":
      tempParams = obj.deepClone(params);
      if (tempParams) {
        tempPramsKeys = Object.keys(tempParams);
        tempPramsKeys.forEach((key) => {
          if (!(tempParams[key] instanceof Array)) {
            plainParams[key] = tempParams[key];
          } else {
            plainParams[key] = tempParams[key].join(",");
          }
        });
      }
      break;
    case "put":
      if (params.plainParams) {
        tempParams = obj.deepClone(params);
        tempPramsKeys = Object.keys(tempParams);
        tempPramsKeys.forEach((key) => {
          if (!(tempParams[key] instanceof Array)) {
            plainParams[key] = tempParams[key];
          } else {
            plainParams[key] = tempParams[key].join(",");
          }
        });
      } else {
        plainData = getParamsByContentType(params, contentType);
      }
      break;
    case "delete":
      if (params.plainParams) {
        tempParams = obj.deepClone(params);
        tempPramsKeys = Object.keys(tempParams);
        tempPramsKeys.forEach((key) => {
          if (!(tempParams[key] instanceof Array)) {
            plainParams[key] = tempParams[key];
          } else {
            plainParams[key] = tempParams[key].join(",");
          }
        });
      } else {
        plainData = obj.deepClone(params); // 默认id放在body里,如果要URL上拼的话请自行拼在url上面
      }
      break;
    case "patch":
      plainData = obj.deepClone(params);
      break;
    case "link":
      responseType = "blob";
      resetMethod = (downloadMethod || "post") as Method;
      if (resetMethod === "get") {
        plainParams = obj.deepClone(params);
      } else {
        plainData = obj.deepClone(params);
      }
      break;
    default:
      console.log("default");
  }
  const accessToken = useUserStore().getTokenFromCookie();

  // const token = Cookies.get(APP_AUTH_TOKEN) || "";
  // if (!method || !url) {
  //   return Promise.reject("请求方法和请求链接不能为空");
  // }

  const locationURI = new URI(window.location.href);
  if (locationURI && locationURI.searchParams?.source === 'FROM_BACKEND' && locationURI.searchParams?.src=== 'preview') {
    plainParams = {...plainParams,...locationURI.searchParams}
  }
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    "Content-Type": contentType,
    "product": "pc",
    "terminal-src": 'WEB',
    "access-token": accessToken || "",
    "lang": lang,
    "ver": '4.24.0',
  };
  requestUrl = `${url}`;
  const isParamsFormData =
    Object.prototype.toString.call(plainParams) !== "[object FormData]";
  const isDataFormData =
    Object.prototype.toString.call(plainData) !== "[object FormData]";

  return new Promise<BaseResponse<T>>((resolve: (value: BaseResponse<T>) => void, reject) => {
    // 如果是静态资源，不携带时间戳
    axios(requestUrl, {
      method: (resetMethod || method) as Method,
      // timeout: 30000,
      timeout: 3000000,
      params:
        isParamsFormData && !notFilterEmptyParam
          ? filterEmptyParam(plainParams)
          : plainParams,
      data:
        isDataFormData && !notFilterEmptyParam
          ? filterEmptyParam(plainData)
          : plainData,
      headers,
      responseType: responseType || "json",
      paramsSerializer:
        // (params: any) => {  // 负责 `params` 序列化的函数
        // // 处理get请求参数带中括号400的问题
        // return qs.stringify(params) as unknown as  ParamsSerializerOptions;
        {
          serialize: (params: any) => {  // 负责 `params` 序列化的函数
            // 处理get请求参数带中括号400的问题
            return qs.stringify(params);
        }
      },
      ...customHeaders,
    })
      .then((res:any) => {
        // 如果是arraybuffer 则是二进制数据(geobuf)，特殊处理
        if (customHeaders && (customHeaders.responseType == 'arraybuffer' || customHeaders.responseType == 'text/html')) {
          resolve(res.data);
        } else if (typeof res == 'string' && (res || "" ).indexOf('base64') > -1) { // 验证码图片
          // @ts-ignore
          resolve({ statusCode: 200, data: res });
        }
        // axios http请求返回
        const { status, data } = res ||{};
        if (status === 200 || status === 201) {
            if (
              res.data &&
              [RequestErrorTypeEnum.TOKEN_INVALID,ResponseStatusCodeEnum.TOKEN_INVALID, ResponseStatusCodeEnum.TOKEN_EXPIRE].includes(res.data.statusCode)
            ) {
              //  如果token失效，清除并重新登录
              //  toLogin('/login');
              EventBus.$emit(AppEventTypeEnum.RE_LOGIN);
            }

            const { mimeType, fileName } = res.data;
            // 如果是文件上传
            if (mimeType && fileName) {
              resolve(res.data);
            }
            // 如果是文件下载
            if (responseType === "blob") {
              handleFileDownload(res, resolve, reject);
              // 如果是接口请求
            } else {
              handleCRUDLogic(res, resolve, reject);
            }
        } else {
          if (res?.data?.comments) {
            Message.error({ message: res.data.comments });
          }
          reject(res?.data);
        }
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status && [502,500,400, 22024017].includes(err.response.status)) {
          messageLimit(err);
        }
        reject(err);
      });
  });
}

// 消息限流
const statusCodeMessage:any = {};
const messageLimit = (err: any) =>{
  if(statusCodeMessage[err.response.status] === undefined) {
    statusCodeMessage[err.response.status] = true;
  }
  if(statusCodeMessage[err.response.status]) {
    Message.error({ message: `${err.response.status}   ${err.response.statusText}` || '服务错误' });
    statusCodeMessage[err.response.status] = false;
  }
  if (statusCodeMessage[err.response.status] === false){
    setTimeout(()=>{
      statusCodeMessage[err.response.status] = true;
    },2000);
  }
}

const limitShowMessage = (statusCode:string, message:string) =>{
  if (!statusCodeMessage[statusCode]){
    statusCodeMessage[statusCode] = true;
    statusCode && Message.error({ message });
    setTimeout(()=>{
      statusCodeMessage[statusCode] = false;
    },2000);
  }
}

// 请求拦截器
const pendingUrl = new Set<string>();
axios.interceptors.request.use((req: any) => {
  return interceptorFlag ? checkExistUrl(req) : req;
}, (err) => { console.log(err); });

// 响应拦截器
axios.interceptors.response.use((res: any) => {
  return interceptorFlag ? deleteExistUrl(res) : res;
}, (err) => { console.log(err); });

/**
 * @params get请求
 * @param {string} url 接口地址
 * @param {object} options {params: object} 请求传参，对应location.search
 */
function httpGet<T>(url = "", options?: any): Promise<BaseResponse<T>> {
  return options?.downloadMethod ? httpDownload<T>(url, options) : request<T>(url, {
    method: "get",
    ...options,
  });
}

/**
 * @description post请求
 * @param {string} url 接口地址
 * @param {object} options {params: object} 请求传参，文件上传 {data: FormData}
 */
function httpPost<T>(url = "", options?: any): Promise<BaseResponse<T>> {
  return options?.downloadMethod ? httpDownload<T>(url, options) : request<T>(url, {
    method: "post",
    ...options,
  });
}

/**
 *
 * @description put请求
 * @param {string} url
 * @param {object} options
 */

function httpPut<T>(url = "", options: any): Promise<BaseResponse<T>> {
  if (options.params && JSON.stringify(options.params) !== '{}'
    && options?.putParamsType === 'splice') {
    url += '?';
    const exceptKeys = options?.exceptKeys || [];
    const paramsKeys = Object.keys(options.params);
    for (let i = 0; i < paramsKeys.length; i++) {
      if (exceptKeys.includes(paramsKeys[i])) {
        continue;
      }
      if (paramsKeys.length === i) {
        url += `${paramsKeys[i]}=${options.params[paramsKeys[i]]}`;
      } else {
        url += `${paramsKeys[i]}=${options.params[paramsKeys[i]]}&`
      }
    }
  }
  return request<T>(url, {
    method: "put",
    ...options,
  });
}

/**
 *
 * @description delete
 * @param {string} url
 * @param {object} options
 */
function httpDelete<T>(url = "", options: any): Promise<BaseResponse<T>> {
  return request<T>(url, {
    method: "delete",
    ...options,
  });
}

// function httpDeleteGet<T>(url = "", options = {}): Promise<BaseResponse<T>> {
//   return request(url, {
//     method: "delete",
//     // url,
//     ...options,
//   });
// }

function httpDownload<T>(url = "", options = {}): Promise<BaseResponse<T>> {
  return request<T>(url, {
    method: "link",
    // url,
    ...options,
  });
}

function httpPatch<T>(url = "", params = {}, token = ""): Promise<BaseResponse<T>> {
  return request<T>(url, {
    method: "patch",
    // url,
    params,
    // token,
  });
}

function httpAll() { }

function checkExistUrl(req: any) {
  if (pendingUrl.has(`${req.method}_${req.url.split('?')[0]}`)) {
    Message.info({ message: '请勿重复点击！' });

    return '';
  }
  pendingUrl.add(`${req.method}_${req.url.split('?')[0]}`);
  return req;
}

function deleteExistUrl(res: any) {
  pendingUrl.delete(`${res.config.method}_${res.config.url.split('?')[0]}`);
  return res;
}

function httpGetArrayBuffer(url:string, responseType = { responseType: 'arraybuffer' }) {
  // return this.requestPromise('GET', targetUrl, {}, {}, responseType);
  return request(url, {
    method: "get",
    // url,
    // params,
    // token,
  },responseType);
}

export {
  request,
  httpGet,
  httpPost,
  httpPut,
  httpDelete,
  httpPatch,
  httpAll,
  httpDownload,
  httpGetArrayBuffer,
  // httpDeleteGet,
};
